import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Checkbox } from "@mui/material";
import classNames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  useClientPlanProfileConfiguration,
  useClientPrintConfiguration,
} from "../../data/global/useProductConfiguration";
import { HCPSpecificMessage } from "../../generate";
import useGetRole, { FAS_ROLE, FRM_ROLE, GENERAL_ROLE } from "../../hooks/useGetRole";
import { useClientRoutesAndLabels } from "../../ui/routes";
import ExpandedActions from "./ExpandedActions";

interface Props {
  client: string;
  index: number;
  hcp: HCPSpecificMessage;
  selectedHcps: HCPSpecificMessage[];
  toggleHcp: (hcp: HCPSpecificMessage, isSelected: boolean) => void;
  expandedRow: null | string;
  setExpandedRow: React.Dispatch<React.SetStateAction<string | null>>;
  isPrint: boolean;
}

const HcpRow: React.FC<Props> = ({
  client,
  index,
  hcp,
  selectedHcps,
  toggleHcp,
  expandedRow,
  setExpandedRow,
  isPrint,
}) => {
  const rowId = `${index}-${hcp.prescID}`;
  const [t] = useTranslation();
  const isOpen = rowId === expandedRow;
  const maxHeight = isOpen ? 1000 : 0;
  // Keep content mounted while collapsing so the CSS height transition has something to animate.
  // Initialize from isOpen so a remount while the row is expanded starts with the correct state.
  const [isExpanded, setIsExpanded] = useState(isOpen);
  const routes = useClientRoutesAndLabels(client);
  const config = useClientPlanProfileConfiguration(client);
  const printConfig = useClientPrintConfiguration(client);
  const { data: role, isFetched } = useGetRole(client);
  const isGeneral = !isFetched || String(role) === GENERAL_ROLE || String(role) === FAS_ROLE;
  const isFRM = !isFetched || String(role) === FRM_ROLE;

  //Set current row as the current row, unless it is already expanded, then minimize all rows
  const expandRow = () => {
    const isCurrentlyOpen = expandedRow === rowId;
    if (!isCurrentlyOpen) {
      setIsExpanded(true);
    }
    setExpandedRow(isCurrentlyOpen ? null : rowId);
  };

  const isSelected = Boolean(selectedHcps.find((selectedHcp) => selectedHcp.prescID === hcp.prescID));
  const isEven = index % 2 === 0;

  const expandButtonClasses = classNames(
    "hidden sm:block w-10 h-10 rounded-full hover:bg-light-blue focus:outline-none focus:ring transform transition-all duration-300 rotate-0",
    {
      "rotate-180": isOpen,
    },
  );
  const outerRowClasses = classNames(
    "border-l-4",
    {
      "border-primary": isOpen,
    },
    {
      "border-light-gray": !isOpen && isEven,
    },
    {
      "border-white": !isOpen && !isEven,
    },
  );
  const innerRowClasses = classNames(
    "flex items-center",
    {
      "bg-light-gray": isEven && !isOpen,
    },
    {
      "border-b border-rules bg-light-blue": isOpen,
    },
  );
  return (
    <div className={outerRowClasses}>
      <div className={innerRowClasses}>
        <div className="w-10 sm:pl-2 sm:w-24 flex items-center justify-between">
          <Checkbox
            inputProps={{ "aria-label": "select row" }}
            onChange={() => toggleHcp(hcp, isSelected)}
            name="selectHcp"
            color="primary"
            checked={isSelected}
          />
          <button
            className={expandButtonClasses}
            onClick={expandRow}
            aria-label={t("specificMessages.toggleRowCollapse")}
          >
            <ExpandMoreIcon />
          </button>
        </div>

        <div className="flex-grow pl-2">
          <button
            onClick={expandRow}
            className="pl-2 w-full text-left text-brand flex py-4 sm:py-1  justify-between items-center hover:underline"
          >
            {hcp.hcpName}
            {(config.hiddenForGeneral && isGeneral) || (isFRM && printConfig.printEnabledForFRM) ? null : (
              <Link
                to={routes.client.dpp.hcps.show.with({ client, id: hcp.prescID })}
                className="hidden sm:block text-slate-gray hover:underline p-4 underline hover:bg-light-blue focus:ring rounded mr-1"
              >
                {t("specificMessages.viewProfile")}
              </Link>
            )}
          </button>
        </div>
      </div>
      <div
        style={{ maxHeight: maxHeight }}
        className="overflow-hidden transition-all duration-300 border-b border-rules sm:pl-24"
        onTransitionEnd={(e) => {
          if (e.target === e.currentTarget && e.propertyName === "max-height" && !isOpen) setIsExpanded(false);
        }}
      >
        {(isOpen || isExpanded) && (
          <ExpandedActions client={client} hcp={hcp} isPrint={isPrint} printConfig={printConfig} />
        )}
      </div>
    </div>
  );
};

export default HcpRow;
